import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import {
  fontSize,
  inputStyles,
  minBreakpointQuery,
  sectionMargins,
  selectStyles,
  selectWrapStyles,
  standardColours,
  standardTransition,
  visuallyHidden,
} from '../styles';
import { Button, Container, Heading } from './ui';
import { slugify } from '../utils';

const StyledCompetitionForm = styled.section`
  ${sectionMargins()}
`;

const StyledHeading = styled(Heading)``;

const StyledText = styled.p`
  margin-top: 15px;
  line-height: 1.9;
`;

const StyledForm = styled.form`
  display: grid;
  gap: 15px;
  margin-top: 20px;

  ${minBreakpointQuery.smedium`
    grid-template-columns: repeat(2, 1fr);
  `}
`;

const StyledField = styled.label`
  width: 100%;

  ${({ isFile }) => {
    if (isFile) {
      return css`
        grid-column: 1 / 3;
      `;
    }
  }}
`;

const StyledLabel = styled.span`
  ${({ visible }) => {
    if (!visible) {
      return css`
        ${visuallyHidden()};
      `;
    } else {
      return css`
        display: block;
        margin-bottom: 10px;
      `;
    }
  }}
`;

const StyledInput = styled.input`
  ${inputStyles()}
  background-color: ${standardColours.lightGrey};

  ${({ isFile }) => {
    if (isFile) {
      return css`
        height: auto;
        padding: 15px;
      `;
    }
  }}
`;

const StyledSelectWrap = styled.div`
  ${selectWrapStyles()};
`;

const StyledSelect = styled.select`
  ${selectStyles()};
  background-color: ${standardColours.lightGrey};
`;

const StyledInputNote = styled.span`
  ${fontSize(14)}
`;

// moved up for checked styling
const StyledCheckboxText = styled.label`
  position: relative;
  display: block;
  padding-left: 30px;
  color: ${standardColours.black};
  line-height: 1.5;
  cursor: pointer;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 20px;
    width: 20px;
    border: 1px solid ${standardColours.black};
  }

  &:after {
    content: '';
    position: absolute;
    top: 3px;
    left: 7px;
    height: 12px;
    width: 6px;
    border: 2px solid ${standardColours.transparent};
    border-top: none;
    border-left: none;
    border-radius: 1px;
    transform: rotate(45deg);
    transition: ${standardTransition('border-color')};
  }
`;

const StyledCheckbox = styled.input`
  ${visuallyHidden()};

  &:checked + ${StyledCheckboxText} {
    &:after {
      border-color: ${standardColours.black};
    }
  }
`;

const StyledSubmittedText = styled.p`
  margin-top: 30px;
  line-height: 1.9;
`;

const StyledSubmit = styled(Button)`
  padding: 10px 15px;
`;

const CompetitionForm = ({
  title,
  text,
  additionalInputFields,
  enableAttachments,
  attachmentLabel,
  submittedText,
}) => {
  const [submitted, setSubmitted] = useState(false);

  const formName = slugify(title);
  const regions = [
    'Cymru Wales',
    'East Midlands',
    'East of England',
    'London',
    'North East & Cumbria',
    'North West',
    'Scotland',
    'South East',
    'South West',
    'West Midlands',
    ' Yorkshire & the Humber',
  ];

  const readFile = file => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = function (error) {
        console.log('Error: ', error);
        reject(error);
      };
    });
  };

  const handleFileChange = e => {
    if (e.target.files.length > 0) {
      const fileSize = (e.target.files[0].size / 1024 / 1024).toFixed(4); // Convert to MB
      // Check size is valid
      if (fileSize > 10) {
        alert('File size is over 10MB');
        e.target.value = '';
      }
    }
  };

  const submitForm = async e => {
    e.preventDefault();

    const form = document.getElementById(formName);
    const formData = new FormData(form);

    const name = formData.get('name');
    const email = formData.get('email');
    const region = formData.get('region');

    let additionalFields = [];
    let attachmentFiles = [];

    if (enableAttachments) {
      const attachments = document.getElementById('attachments').files;

      for (const attachment of attachments) {
        const file = await readFile(attachment);
        attachmentFiles.push({
          name: attachment.name,
          type: attachment.type,
          data: file,
        });
      }
    }

    // get the additional fields
    additionalInputFields.forEach(({ name, type }) => {
      additionalFields.push({
        name: name,
        type: type,
        value: formData.get(slugify(name)),
      });
    });

    fetch('/api/sendMail', {
      method: 'POST',
      body: JSON.stringify({
        name: name,
        email: email,
        region: region,
        formName: title,
        additionalFields: JSON.stringify(additionalFields),
        attachmentFiles: JSON.stringify(attachmentFiles),
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw response;
        }
        setSubmitted(true);
      })
      .catch(error => error.json().then(err => console.log(err.msg)));
  };

  return (
    <StyledCompetitionForm>
      <Container narrow={true}>
        <StyledHeading>{title}</StyledHeading>
        {text && <StyledText>{text}</StyledText>}
        {submitted ? (
          <StyledSubmittedText>{submittedText}</StyledSubmittedText>
        ) : (
          <StyledForm
            id={formName}
            name={formName}
            method="POST"
            onSubmit={e => {
              submitForm(e);
            }}
          >
            <StyledField>
              <StyledLabel>Name</StyledLabel>
              <StyledInput
                type="text"
                name="name"
                placeholder="Name*"
                required
              />
            </StyledField>
            <StyledField>
              <StyledLabel>Email Address</StyledLabel>
              <StyledInput
                type="email"
                name="email"
                placeholder="Email Address*"
                required
              />
            </StyledField>
            <StyledField>
              <StyledLabel>Region</StyledLabel>
              <StyledSelectWrap>
                <StyledSelect name="region" required>
                  <option value="" selected disabled>
                    SELECT A REGION*
                  </option>
                  {regions.map(region => (
                    <option value={region}>{region}</option>
                  ))}
                </StyledSelect>
              </StyledSelectWrap>
            </StyledField>
            {additionalInputFields.length > 0 &&
              additionalInputFields.map(({ name, required }, id) => {
                const fieldName = slugify(name);
                return (
                  <StyledField key={id}>
                    <StyledLabel>{name}</StyledLabel>
                    <StyledInput
                      type="text"
                      name={fieldName}
                      placeholder={`${name}${required ? '*' : ''}`}
                      required={required}
                    />
                  </StyledField>
                );
              })}
            {enableAttachments && (
              <StyledField isFile={true}>
                <StyledLabel visible={true}>
                  {attachmentLabel ? attachmentLabel : 'Upload Attachment'}
                </StyledLabel>
                <StyledInput
                  type="file"
                  id="attachments"
                  name="attachments"
                  accept="image/*,video/*"
                  isFile={true}
                  onChange={e => handleFileChange(e)}
                />
                <StyledInputNote>Max upload size: 10MB</StyledInputNote>
              </StyledField>
            )}
            <StyledField>
              <StyledCheckbox
                type="checkbox"
                id="over-18"
                name="Over 18"
                value={true}
                required
              />
              <StyledCheckboxText for="over-18">
                I agree to being over 18
              </StyledCheckboxText>
            </StyledField>
            <StyledSubmit type="submit" title="Submit" alt={true}>
              Submit
            </StyledSubmit>
          </StyledForm>
        )}
      </Container>
    </StyledCompetitionForm>
  );
};

export default CompetitionForm;
